import { useState, useRef, useEffect } from 'react';
import { isObject } from 'underscore';

import { UploadJob } from '@/store/slices/content/types';
import { GetUploadEndpoint, SetItemContentType, UploadFileToProvider } from '@ui/uploads/validations';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { getUser } from '@/store/slices/user/selectors';
import { UploadVideo } from '@/connection/content/videos';
import toastTrigger from '@ui/toast/toast-trigger';
import { selectUploadJobs } from '@/store/slices/content/selectors';
import { addUploadedVideo, finishUploadJob, videoConversionUpdate } from '@/store/slices/content';
import { getVideoDuration, getVideoThumbnail } from '@/micros/homepage/content/utils/upload-utils';
import { removeInvalidFilenameCharacters } from '../utils';
import { EToastTypes } from '@ui/toast/toast.types';
import { shouldUseMultipartUpload, UploadFileMultipart } from '@/connection/multipart-uploads/multipart-uploads';
import { findResumableUpload } from '@/connection/multipart-uploads/resume-store';
import { MultipartUploadInterruptedError } from '@/connection/multipart-uploads/errors';
import { selectProjectChannel } from '@/store/slices/project/selectors';
import { useSocket } from '@/connection/socket/hooks/use-socket';
import type { StaticVideo } from '@/types/video.types';

interface InputRefs {
  [key: string]: HTMLInputElement | null;
}

//TODO all commented code in this file is related to connecting socket messages

export interface UploadingJob extends UploadJob {
  progress: number;
  done: boolean;
  totalUploadedBytes: number;
  lastUploadedBytes: number;
  totalBytesToUpload: number;
  filename: string;
}

const useUploader = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const uploadJobs = useAppSelector(selectUploadJobs);
  const projectChannel = useAppSelector(selectProjectChannel);
  const socket = useSocket(`admin-${projectChannel}`);

  const [jobsInProgress, setJobsInProgress] = useState<UploadingJob[]>([]);

  const inputElements = useRef<InputRefs>({});

  useEffect(() => {
    const onVideoConversionComplete = ({ data }: { data: StaticVideo }) => {
      toastTrigger('Conversion complete', { description: `${data.name} is ready for use` });
      dispatch(videoConversionUpdate(data));
    };

    const onVideoConversionError = ({ data }: { data: StaticVideo }) => {
      toastTrigger('Conversion failed', {
        description: `${data.name} failed to convert. Please try uploading the video again.`,
        type: 'error',
      });
      dispatch(videoConversionUpdate(data));
    };

    const onVideoConversionProgress = ({ data }: { data: StaticVideo }) => {
      dispatch(videoConversionUpdate(data));
    };

    socket.addListener('video-conversion-progress', onVideoConversionProgress);
    socket.addListener('video-conversion-complete', onVideoConversionComplete);
    socket.addListener('video-conversion-error', onVideoConversionError);

    return () => {
      socket.removeListener('video-conversion-progress', onVideoConversionProgress);
      socket.removeListener('video-conversion-complete', onVideoConversionComplete);
      socket.removeListener('video-conversion-error', onVideoConversionError);
    };
  }, [socket, dispatch]);

  useEffect(() => {
    const currentJobs = jobsInProgress.map((job) => job.uuid);

    // here we want to track the newly added ones so we can trigger their input elements
    const newJobs: UploadingJob[] = uploadJobs
      .filter((job) => !currentJobs.includes(job.uuid))
      .map((job) => {
        const uuid = job.uuid;

        // wait until after the next render to show the element
        setTimeout(() => {
          inputElements.current[uuid]?.click();
        }, 500);

        return {
          ...job,
          progress: 0,
          done: false,
          totalUploadedBytes: 0,
          lastUploadedBytes: 0,
          totalBytesToUpload: 0,
          filename: '',
        };
      });

    setJobsInProgress([...jobsInProgress, ...newJobs]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadJobs]);

  const handleUploadProgress = (uuid: string) => (total: number) =>
    setJobsInProgress((_jobs) =>
      _jobs.map((job) => {
        if (job.uuid === uuid) {
          job.totalUploadedBytes += total - job.lastUploadedBytes;
          job.progress = Math.floor((job.totalUploadedBytes / job.totalBytesToUpload) * 100);
          job.lastUploadedBytes = total;
        }
        return job;
      })
    );

  const handleFileChange =
    (job: UploadingJob) =>
    async ({ target: { files } }: React.ChangeEvent<HTMLInputElement>) => {
      let uuid: string;
      let filename = '';
      try {
        if (!files?.length || !user || !projectChannel) return;

        const fileToUpload = files[0];
        uuid = job.uuid;
        const cleanedName = removeInvalidFilenameCharacters(fileToUpload.name);
        filename = fileToUpload.name;
        if (job.onStarted) job.onStarted();

        setJobsInProgress((jobsInProgress) =>
          jobsInProgress.map((_job) => {
            if (_job.uuid === uuid) {
              _job.uploading = true;
              _job.totalBytesToUpload = fileToUpload.size;
              _job.filename = cleanedName;
            }

            return _job;
          })
        );

        const duration = await getVideoDuration(fileToUpload);
        let finalUrl: string;

        // if file is large enough for multipart, upload it via multipart upload
        if (shouldUseMultipartUpload(fileToUpload)) {
          // picking a file we were partway through - most likely after a reload or a dropped
          // connection - carries on from the part it reached instead of re-sending the whole thing
          const resume = await findResumableUpload(projectChannel, fileToUpload);

          if (resume) {
            const done = resume.uploadedParts.length * resume.partSize;
            toastTrigger('Resuming upload', {
              description: `${cleanedName} is ${Math.floor((done / fileToUpload.size) * 100)}% uploaded, picking up where it left off.`,
              type: EToastTypes.info,
            });
          }

          const { url } = await UploadFileMultipart({
            channelId: projectChannel,
            file: fileToUpload,
            filename: cleanedName,
            onProgress: handleUploadProgress(uuid),
            resume,
          });
          finalUrl = url;
        } else {
          // if file is NOT large enough, get the endpoint and upload it
          const endpoint = await GetUploadEndpoint({ filename: cleanedName });

          if (isObject(endpoint) && endpoint.url) {
            finalUrl = await UploadFileToProvider(
              {
                url: endpoint.url,
                fields: endpoint.fields,
                cdnHost: endpoint?.cdnHost,
              },
              fileToUpload,
              handleUploadProgress(uuid),
              cleanedName
            );

            SetItemContentType({ filename: endpoint.fields.Key, contentType: fileToUpload.type }).catch(console.error);
          } else {
            // unable to get endpoint, break here
            throw new Error(`Unable to upload file!`);
          }
        }

        // Generate the thumbnail for the video
        const thumb = (await getVideoThumbnail(fileToUpload)) as Blob;
        const thumbFile = new File([thumb], 'thumbnail.jpg', { type: 'image/jpeg' });
        const filenameParts = thumbFile.name.split('.');
        const suffix = filenameParts[filenameParts.length - 1];
        const reader = new FileReader();
        reader.onload = () => {
          if (typeof reader.result === 'string') {
            // create an image element and load in the image in base64
            const image = document.createElement('img');
            image.onload = async () => {
              const filename = `${Date.now()}_w${image.width}_h${image.height}.${suffix}`;

              const thumbnailEndpoint = await GetUploadEndpoint({ filename: filename, contentType: 'image/jpeg' });

              if (isObject(thumbnailEndpoint) && thumbnailEndpoint.url) {
                const thumbnailUrl = await UploadFileToProvider(
                  {
                    url: thumbnailEndpoint.url,
                    fields: thumbnailEndpoint.fields,
                    cdnHost: thumbnailEndpoint?.cdnHost,
                  },
                  thumb,
                  handleUploadProgress(uuid),
                  cleanedName
                );

                SetItemContentType({ filename: thumbnailEndpoint.fields.Key, contentType: 'image/jpeg' }).catch(
                  console.error
                );
                const createdVideo = await UploadVideo(user.active_channel, {
                  type: 'uploaded',
                  name: cleanedName,
                  source: finalUrl,
                  use_thumbnail: false,
                  image: thumbnailUrl,
                  original_image: thumbnailUrl,
                  duration: duration,
                });

                toastTrigger(`${cleanedName} Uploaded`, {
                  description: 'Please allow several minutes for the video to be available for playback.',
                  type: EToastTypes.info,
                  autoClose: 10000,
                });

                dispatch(addUploadedVideo(createdVideo));

                // call callback with the created video
                setJobsInProgress((jobs) =>
                  jobs.map((job) => {
                    if (job.uuid === uuid) {
                      job.done = true;
                      job.onComplete(createdVideo);
                    }
                    return job;
                  })
                );
              }
            };
            image.src = reader.result;
          }
        };

        reader.readAsDataURL(thumbFile);

        // allow "Done" message for 5 seconds, then remove from list
        setTimeout(() => {
          setJobsInProgress((jobs) =>
            jobs.filter((job) => {
              return job.uuid !== uuid;
            })
          );
          dispatch(finishUploadJob(uuid));
        }, 5000);
      } catch (e) {
        console.error(e);
        setJobsInProgress((jobs) =>
          jobs.filter((_job) => {
            return _job.uuid !== uuid;
          })
        );

        // an interrupted upload keeps its place, so the fix is to pick the same file again rather
        // than to start over - worth saying, because the two look identical from here
        const interrupted = e instanceof MultipartUploadInterruptedError;

        toastTrigger(interrupted ? 'Upload interrupted' : 'Upload error', {
          description: interrupted
            ? `Lost connection while uploading ${filename ? filename : 'your content'}. Select the same file again to carry on from where it stopped.`
            : `There was an error uploading ${filename ? filename : 'your content'}, please try again.`,
          type: EToastTypes.error,
          autoClose: 10000,
        });
      }
    };

  return {
    jobsInProgress,
    inputElements,
    handleFileChange,
  };
};

export default useUploader;
